import { jsx as _jsx } from "react/jsx-runtime";
import { defineCatalog } from '@json-render/core';
import { defineRegistry } from '@json-render/react';
import { schema } from '@json-render/react/schema';
import { shadcnComponents } from '@json-render/shadcn';
import { shadcnComponentDefinitions } from '@json-render/shadcn/catalog';
import { BarChartPrimitive, BarChartPropsSchema } from './primitives/bar-chart.js';
import { CalloutPrimitive, CalloutPropsSchema } from './primitives/callout.js';
import { DiagramPrimitive, DiagramPropsSchema } from './primitives/diagram.js';
import { ItemListPrimitive, ItemListPropsSchema } from './primitives/item-list.js';
import { KeyValuePrimitive, KeyValuePropsSchema } from './primitives/key-value.js';
import { LinkListPrimitive, LinkListPropsSchema } from './primitives/link-list.js';
import { MarkdownPrimitive, MarkdownPropsSchema } from './primitives/markdown.js';
import { MetricGridPrimitive, MetricGridPropsSchema } from './primitives/metric-grid.js';
import { QuotePrimitive, QuotePropsSchema } from './primitives/quote.js';
import { StackedBarChartPrimitive, StackedBarChartPropsSchema, } from './primitives/stacked-bar-chart.js';
import { TablePrimitive, TablePropsSchema } from './primitives/table.js';
import { TimelinePrimitive, TimelinePropsSchema } from './primitives/timeline.js';
import { UnsupportedVisualizationPrimitive, UnsupportedVisualizationPropsSchema, } from './primitives/unsupported-visualization.js';
import { PrimitiveErrorBoundary } from './lib/error-boundary.js';
/**
 * The M2 generative-layout catalog: the 13 presentation primitives used by
 * `DigestView` (snake_case keys) plus a set of framework-maintained
 * `@json-render/shadcn` layout/presentation components (PascalCase keys) so
 * generative specs can compose real layouts. Registered with `defineCatalog()`
 * from `@json-render/react`'s flat element-tree schema (`root` + `elements`).
 *
 * Written as an explicit literal (not built by iterating a shared registry
 * object) so each component keeps its precise Zod props type through
 * `defineCatalog`'s generic inference — that's what makes `defineRegistry`
 * below type-check each component against its own props.
 */
export const almanacCatalog = defineCatalog(schema, {
    components: {
        markdown: {
            props: MarkdownPropsSchema,
            description: 'Freeform sanitized GitHub-flavored markdown. Use for prose.',
        },
        item_list: {
            props: ItemListPropsSchema,
            description: 'A list of feedback-capable items with title/detail/links.',
        },
        callout: {
            props: CalloutPropsSchema,
            description: 'A highlighted banner (info/warn/success/danger) with an optional link.',
        },
        metric_grid: {
            props: MetricGridPropsSchema,
            description: 'A grid of labeled KPI values, optionally colored by state.',
        },
        table: {
            props: TablePropsSchema,
            description: 'A simple text table with columns and rows.',
        },
        timeline: {
            props: TimelinePropsSchema,
            description: 'A vertical list of time-ordered entries.',
        },
        link_list: {
            props: LinkListPropsSchema,
            description: 'A list of labeled links (http/https/mailto only).',
        },
        key_value: {
            props: KeyValuePropsSchema,
            description: 'A list of label/value pairs.',
        },
        quote: {
            props: QuotePropsSchema,
            description: 'A pull-quote with an optional citation.',
        },
        bar_chart: {
            props: BarChartPropsSchema,
            description: 'Horizontal percent bars, optionally colored by state.',
        },
        stacked_bar_chart: {
            props: StackedBarChartPropsSchema,
            description: 'Horizontal bars segmented by series, with an optional legend.',
        },
        diagram: {
            props: DiagramPropsSchema,
            description: 'A mermaid diagram request. Not rendered as a real diagram in v1 — always pair it with a fallback_block that conveys the same information as a simple block.',
        },
        unsupported_visualization_request: {
            props: UnsupportedVisualizationPropsSchema,
            description: 'Use when asked for a visualization this catalog cannot render. Explain why in `reason` and provide an equivalent fallback_block.',
        },
        // Framework-maintained shadcn/ui layout + presentation components, so
        // generative specs can compose real layouts around the domain blocks.
        // Kept under their PascalCase framework keys (specs authored against the
        // shadcn docs reference them by these names).
        Card: shadcnComponentDefinitions.Card,
        Stack: shadcnComponentDefinitions.Stack,
        Grid: shadcnComponentDefinitions.Grid,
        Separator: shadcnComponentDefinitions.Separator,
        Tabs: shadcnComponentDefinitions.Tabs,
        Heading: shadcnComponentDefinitions.Heading,
        Text: shadcnComponentDefinitions.Text,
        Badge: shadcnComponentDefinitions.Badge,
        Alert: shadcnComponentDefinitions.Alert,
        Progress: shadcnComponentDefinitions.Progress,
    },
    actions: {},
});
/**
 * Component registry for `<Renderer registry={almanacRegistry} />`. Each
 * entry is wrapped in {@link PrimitiveErrorBoundary} so a single malformed
 * element can't crash the rest of the generative layout — mirrors the
 * safety `renderBlock`/`renderSimpleBlock` give the M1 digest path.
 */
export const { registry: almanacRegistry } = defineRegistry(almanacCatalog, {
    components: {
        markdown: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "markdown", children: _jsx(MarkdownPrimitive, { ...props }) })),
        item_list: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "item_list", children: _jsx(ItemListPrimitive, { ...props }) })),
        callout: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "callout", children: _jsx(CalloutPrimitive, { ...props }) })),
        metric_grid: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "metric_grid", children: _jsx(MetricGridPrimitive, { ...props }) })),
        table: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "table", children: _jsx(TablePrimitive, { ...props }) })),
        timeline: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "timeline", children: _jsx(TimelinePrimitive, { ...props }) })),
        link_list: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "link_list", children: _jsx(LinkListPrimitive, { ...props }) })),
        key_value: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "key_value", children: _jsx(KeyValuePrimitive, { ...props }) })),
        quote: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "quote", children: _jsx(QuotePrimitive, { ...props }) })),
        bar_chart: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "bar_chart", children: _jsx(BarChartPrimitive, { ...props }) })),
        stacked_bar_chart: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "stacked_bar_chart", children: _jsx(StackedBarChartPrimitive, { ...props }) })),
        diagram: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "diagram", children: _jsx(DiagramPrimitive, { ...props }) })),
        unsupported_visualization_request: ({ props }) => (_jsx(PrimitiveErrorBoundary, { label: "unsupported_visualization_request", children: _jsx(UnsupportedVisualizationPrimitive, { ...props }) })),
        // shadcn impls are passed through their framework component context
        // ({ props, children, emit, ... }) and wrapped in the same error boundary
        // as the domain blocks so one malformed element can't crash the layout.
        Card: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Card", children: _jsx(shadcnComponents.Card, { ...ctx }) })),
        Stack: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Stack", children: _jsx(shadcnComponents.Stack, { ...ctx }) })),
        Grid: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Grid", children: _jsx(shadcnComponents.Grid, { ...ctx }) })),
        Separator: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Separator", children: _jsx(shadcnComponents.Separator, { ...ctx }) })),
        Tabs: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Tabs", children: _jsx(shadcnComponents.Tabs, { ...ctx }) })),
        Heading: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Heading", children: _jsx(shadcnComponents.Heading, { ...ctx }) })),
        Text: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Text", children: _jsx(shadcnComponents.Text, { ...ctx }) })),
        Badge: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Badge", children: _jsx(shadcnComponents.Badge, { ...ctx }) })),
        Alert: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Alert", children: _jsx(shadcnComponents.Alert, { ...ctx }) })),
        Progress: (ctx) => (_jsx(PrimitiveErrorBoundary, { label: "Progress", children: _jsx(shadcnComponents.Progress, { ...ctx }) })),
    },
});
/** Generate the AI system prompt for {@link almanacCatalog}. */
export function catalogPrompt(options) {
    return almanacCatalog.prompt(options);
}
export { autoFixSpec, validateSpec } from '@json-render/core';
